import { motion } from "motion/react";
import { Plus } from "lucide-react";
import type { PersonId } from "../../../data/people";
import { dayTaskCount, isPastDay, tasksLabel } from "../../../lib/domain";
import { spring } from "../../../lib/motion";
import { dayPlan, HOURS, LUNCH, VIEW_END, VIEW_START, WORK_END, type Slot } from "../../../lib/schedule";
import { dayOf, hm, hourOf, timeOf } from "../../../lib/time";
import { cn } from "../../../lib/utils";
import type { Busy, Status, Task } from "../../../types";

/** Выбор монтажёра и дня при постановке задачи: черновик показываем прямо в сетке */
export interface PickSpec {
  editor: PersonId;
  day: number;
  title: string;
  estimate: number;
  /** сколько задач в новой постановке — для счётчика «станет N задач» */
  size?: number;
  excludeTaskId?: string;
  onPick: (editor: PersonId, day: number) => void;
}

export interface CalendarProps {
  now: number;
  tasks: Task[];
  busy: Busy[];
  /** режим выбора монтажёра и дня при постановке задачи */
  pick?: PickSpec;
  onTaskClick?: (t: Task) => void;
  onAdd?: (editor: PersonId, day: number) => void;
  /** внутри модалки — ниже по высоте */
  compact?: boolean;
}

/** Цвет блока — без обводок: заливка и полоска слева */
const TONE: Record<Status, string> = {
  open: "bg-muted text-muted-foreground shadow-[inset_3px_0_0_0_var(--ring)]",
  wait: "bg-[repeating-linear-gradient(135deg,var(--wait-soft)_0_7px,#fff3d1_7px_14px)] text-wait-fg shadow-[inset_3px_0_0_0_var(--wait)]",
  work: "bg-work-soft text-work-fg shadow-[inset_3px_0_0_0_var(--work)]",
  review: "bg-review-soft text-review-fg shadow-[inset_3px_0_0_0_var(--review)]",
  rework: "bg-rework-soft text-rework-fg shadow-[inset_3px_0_0_0_var(--rework)]",
  publish: "bg-publish-soft text-publish-fg shadow-[inset_3px_0_0_0_var(--publish)]",
  done: "bg-done-soft text-done-fg shadow-[inset_3px_0_0_0_var(--done)]",
};
const KIND: Record<Exclude<Slot["kind"], "task">, string> = {
  busy: "bg-[repeating-linear-gradient(135deg,#efefef_0_6px,#e7e7e7_6px_12px)] text-muted-foreground",
  meet: "bg-secondary text-muted-foreground shadow-[inset_3px_0_0_0_var(--ring)]",
  proposal: "bg-[repeating-linear-gradient(135deg,var(--review-soft)_0_7px,#ebe4ff_7px_14px)] text-review-fg shadow-[inset_3px_0_0_0_var(--review)]",
  draft: "bg-primary text-primary-foreground shadow-mmd",
};
export const slotTone = (s: Slot) => (s.kind === "task" ? TONE[s.task!.status] : KIND[s.kind]);

const WORD: Record<Status, string> = { open: "свободная", wait: "ждёт ответа", work: "в работе", review: "на проверке", rework: "правки", publish: "ждёт ссылок", done: "готово" };

export function slotMeta(s: Slot, seg: [number, number] = s.segs[0]) {
  if (s.kind === "task" && s.late) return `не успевает к ${timeOf(s.task!.due)}`;
  if (s.kind === "task") return `${hm(seg[0])}–${hm(seg[1])} · ${WORD[s.task!.status]}`;
  if (s.kind === "proposal") return "предложен перенос сюда";
  if (s.kind === "draft") return "новая задача";
  if (s.kind === "meet") return `${hm(seg[0])}–${hm(seg[1])}`;
  return "не из воронки";
}

/** Один кусок блока в колонке времени */
function SlotSeg({ slot, seg, first, hourPx, viewEnd, inert, onTaskClick }: { slot: Slot; seg: [number, number]; first: boolean; hourPx: number; viewEnd: number; inert?: boolean; onTaskClick?: (t: Task) => void }) {
  const [a, b] = seg;
  if (a >= viewEnd) return null;
  const top = (a - VIEW_START) * hourPx + 1;
  const height = Math.max(16, (Math.min(b, viewEnd) - a) * hourPx - 2);
  const t = slot.task;
  const clickable = !inert && !!t && !!onTaskClick;
  // В низком блоке (день длинный, час мельче) время и статус — в одну строку с названием
  const tall = height >= 38;
  return (
    <motion.button
      type="button"
      layoutId={slot.kind === "draft" && first ? "draft-block" : first && t && !inert ? `cal-${t.id}` : undefined}
      transition={spring.snappy}
      onClick={(e) => {
        e.stopPropagation();
        if (clickable) onTaskClick!(t!);
      }}
      title={`${slot.title} · ${hm(a)}–${hm(b)}`}
      style={{ top, height, borderRadius: 10 }}
      className={cn(
        "cal-block absolute inset-x-1 flex overflow-hidden px-2 text-left",
        tall ? "flex-col gap-0.5 py-1" : "items-center gap-1.5",
        slotTone(slot),
        clickable ? "cursor-pointer hover:brightness-[0.97]" : "cursor-default",
        inert && "pointer-events-none",
        !first && "opacity-75",
      )}
    >
      <span className={cn("truncate text-xs font-semibold leading-tight", !tall && "max-w-full shrink-0")}>{slot.title}</span>
      {height >= 20 && (
        <span className={cn("min-w-0 truncate text-[11px] leading-tight opacity-80", slot.late && "font-semibold text-rework-fg opacity-100")}>
          {first ? slotMeta(slot, seg) : `${hm(a)}–${hm(b)} · продолжение`}
        </span>
      )}
    </motion.button>
  );
}

/** Подписи часов слева от сетки */
export function HourGutter({ hourPx, viewEnd = VIEW_END }: { hourPx: number; viewEnd?: number }) {
  const hours = viewEnd === VIEW_END ? HOURS : Array.from({ length: viewEnd - VIEW_START + 1 }, (_, i) => VIEW_START + i);
  return (
    <div className="relative" style={{ height: (viewEnd - VIEW_START) * hourPx }}>
      {hours.map((h) => (
        <span key={h} className="absolute right-2 -translate-y-1/2 text-[11px] font-medium tabular-nums text-muted-foreground" style={{ top: (h - VIEW_START) * hourPx }}>
          {h}:00
        </span>
      ))}
      {/* в мелкой сетке подпись налезла бы на следующий час — обед и так виден по штриховке */}
      {hourPx >= 32 && (
        <span className="absolute right-2 text-[10px] text-muted-foreground/70" style={{ top: (LUNCH[0] - VIEW_START) * hourPx + 10 }}>
          обед
        </span>
      )}
    </div>
  );
}

/** Колонка «монтажёр × день» по часам: блоки задач, обед, переработка, линия «сейчас» */
export function TimeColumn({
  editor,
  day,
  now,
  tasks,
  busy,
  hourPx,
  viewEnd = VIEW_END,
  pick,
  onTaskClick,
  onAdd,
}: {
  editor: PersonId;
  day: number;
  now: number;
  tasks: Task[];
  busy: Busy[];
  hourPx: number;
  /** низ сетки: если задачи не влезают до 19:00, сетка продлевается */
  viewEnd?: number;
  pick?: PickSpec;
  onTaskClick?: (t: Task) => void;
  onAdd?: (editor: PersonId, day: number) => void;
}) {
  const past = isPastDay(day, now);
  const selected = !!pick && pick.editor === editor && pick.day === day;
  const plan = dayPlan(editor, day, tasks, busy, {
    draft: selected ? { editor, day, title: pick!.title, estimate: pick!.estimate } : undefined,
    excludeTaskId: pick?.excludeTaskId,
  });
  const clickable = !past && (pick ? !selected : !!onAdd);
  const nowH = day === dayOf(now) ? hourOf(now) : null;
  const H = (viewEnd - VIEW_START) * hourPx;
  const y = (h: number) => (Math.min(Math.max(h, VIEW_START), viewEnd) - VIEW_START) * hourPx;
  const hintTop = Math.min(y(plan.freeAt), H - hourPx) + 1;

  return (
    <div
      data-col={`${editor}-${day}`}
      onClick={clickable ? () => (pick ? pick.onPick(editor, day) : onAdd!(editor, day)) : undefined}
      style={{ height: H, backgroundImage: `repeating-linear-gradient(to bottom, rgb(10 10 10 / 0.06) 0 1px, transparent 1px ${hourPx}px)` }}
      className={cn(
        "group relative rounded-[16px] transition-[background-color,box-shadow] duration-200",
        past ? "bg-muted/40 opacity-55" : "bg-muted/60",
        clickable && "cursor-pointer hover:bg-muted",
        selected && "bg-background shadow-[0_0_0_2px_var(--primary),0_6px_18px_rgb(10_10_10/0.08)]",
      )}
    >
      <div
        className="pointer-events-none absolute inset-x-0 bg-[repeating-linear-gradient(135deg,rgb(10_10_10/0.035)_0_6px,transparent_6px_12px)]"
        style={{ top: y(LUNCH[0]), height: (LUNCH[1] - LUNCH[0]) * hourPx }}
      />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 rounded-b-[16px] bg-rework/[0.06]" style={{ top: y(WORK_END) }} />
      {plan.slots.flatMap((s) => s.segs.map((seg, i) => <SlotSeg key={`${s.key}-${i}`} slot={s} seg={seg} first={i === 0} hourPx={hourPx} viewEnd={viewEnd} inert={!!pick} onTaskClick={onTaskClick} />))}
      {clickable && (
        <div
          className="pointer-events-none absolute inset-x-1 hidden items-center justify-center gap-1 rounded-[10px] bg-background text-[11px] font-medium text-muted-foreground shadow-mxs group-hover:flex group-has-[.cal-block:hover]:hidden"
          style={{ top: hintTop, height: hourPx - 2 }}
        >
          <Plus className="size-3" />
          {pick ? `сюда · станет ${tasksLabel(dayTaskCount(editor, day, tasks, busy, pick.excludeTaskId) + (pick.size ?? 1))}` : "поставить задачу"}
        </div>
      )}
      {nowH !== null && nowH >= VIEW_START && nowH <= viewEnd && (
        <div className="pointer-events-none absolute inset-x-0 z-10 h-0.5 bg-rework" style={{ top: y(nowH) - 1 }}>
          <span className="absolute -left-1 -top-[3px] size-2 rounded-full bg-rework" />
        </div>
      )}
    </div>
  );
}
